import { gql, useApolloClient, useQuery } from '@apollo/client'
import cx from 'classnames'
import { useToast } from 'components/Dashboard/Toast'
import Modal from 'components/Modal'
import TextInput from 'components/TextInput'
import { format, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { getHours, getMinutes, parseISO, setHours, setMilliseconds, setMinutes, setSeconds } from 'date-fns/fp'
import { Field, Form, Formik } from 'formik'
import { flow, range } from 'lodash/fp'
import {
  AmendAndResendCertificateMutation,
  AmendAndResendCertificateMutationVariables,
} from './__generated__/AmendAndResendCertificateMutation'
import {
  KitRegistrationDetailsQuery,
  KitRegistrationDetailsQueryVariables,
} from './__generated__/KitRegistrationDetailsQuery'

const kitRegDetailsQuery = gql`
  query KitRegistrationDetailsQuery($kitId: ID!) {
    kitById(id: $kitId) {
      id
      registrationInfo {
        firstName
        lastName
        dob
        email
        passportNumber
        trackingNumber
      }
      sampleDate
    }
  }
`

const amendAndResendCertificateMutation = gql`
  mutation AmendAndResendCertificateMutation($input: AmendAndResendCertificateInput!) {
    amendAndResendCertificate(input: $input) {
      success
      kit {
        id
        registrationInfo {
          firstName
          lastName
          dob
          email
          passportNumber
          trackingNumber
        }
        sampleDate
      }
    }
  }
`

interface FormValues {
  firstName: string
  lastName: string
  passportNumber: string
  dob: string
  email: string
  sampleDate: string
  sampleTimeHours: number
  sampleTimeMinutes: number
}

const sampleDateToParts = (sampleDate: string) => {
  const sampleDateInUk = utcToZonedTime(parseISO(sampleDate), 'Europe/London')

  return {
    sampleDate: format(sampleDateInUk, 'yyyy-MM-dd'),
    sampleTimeHours: getHours(sampleDateInUk),
    sampleTimeMinutes: getMinutes(sampleDateInUk),
  }
}

const getSampleDateTime = (values: { sampleDate: string; sampleTimeHours: number; sampleTimeMinutes: number }): Date =>
  flow([
    parseISO,
    setHours(values.sampleTimeHours),
    setMinutes(values.sampleTimeMinutes),
    setSeconds(0),
    setMilliseconds(0),
    (d) => zonedTimeToUtc(d, 'Europe/London'),
  ])(values.sampleDate)

const AmendAndResendCertificateModal = ({ kitId, onRequestClose }: { kitId: string; onRequestClose: () => void }) => {
  const { loading, error, data } = useQuery<KitRegistrationDetailsQuery, KitRegistrationDetailsQueryVariables>(
    kitRegDetailsQuery,
    { variables: { kitId } }
  )

  const client = useApolloClient()

  const { addToast } = useToast()

  if (error) throw error

  const kit = data?.kitById

  return (
    <Modal isShown={true} onRequestClose={onRequestClose}>
      <div className="modal-card">
        <header className="modal-card-head">
          <p className="modal-card-title">Amend & Resend Certificate</p>
          <button onClick={() => onRequestClose()} className="delete" aria-label="close"></button>
        </header>
        {loading && <p>Loading...</p>}

        {kit && (
          <Formik<FormValues>
            initialValues={{
              firstName: kit.registrationInfo.firstName.toUpperCase(),
              lastName: kit.registrationInfo.lastName.toUpperCase(),
              passportNumber: kit.registrationInfo.passportNumber,
              email: kit.registrationInfo.email,
              dob: kit.registrationInfo.dob,
              ...sampleDateToParts(kit.sampleDate),
            }}
            onSubmit={async (values) => {
              try {
                const resp = await client.mutate<
                  AmendAndResendCertificateMutation,
                  AmendAndResendCertificateMutationVariables
                >({
                  mutation: amendAndResendCertificateMutation,
                  variables: {
                    input: {
                      kitId: kitId,
                      firstName: values.firstName,
                      lastName: values.lastName,
                      dob: values.dob,
                      email: values.email,
                      passportNumber: values.passportNumber,
                      sampleDate: getSampleDateTime(values).toISOString(),
                    },
                  },
                })

                if (resp.data.amendAndResendCertificate?.success) {
                  addToast({
                    messageType: 'success',
                    title: 'Success',
                    content: 'Amended and resent',
                  })

                  onRequestClose()
                }
              } catch (err) {
                addToast({
                  messageType: 'error',
                  title: 'Error',
                  content: `Failed to amend & resend: ${err.message}`,
                })
              }
            }}
          >
            {({ initialValues, isSubmitting }) => (
              <Form>
                <section className="modal-card-body">
                  <div className="is-flex">
                    <div className="is-flex-grow-1 mr-1">
                      <TextInput name="firstName" label={`First Name (currently: "${initialValues.firstName}")`} />
                    </div>

                    <div className="is-flex-grow-1 ml-1">
                      <TextInput name="lastName" label={`Last Name (currently: "${initialValues.lastName}")`} />
                    </div>
                  </div>
                  <TextInput
                    name="passportNumber"
                    label={`Passport Number (currently: "${initialValues.passportNumber}")`}
                  />
                  <TextInput name="dob" label={`DOB (currently: "${initialValues.dob}"). YYYY-MM-DD`} />
                  <TextInput
                    name="email"
                    label={
                      <>
                        Email address (currently: "{initialValues.email}").{' '}
                        <span className="has-text-danger">This will be UNVERIFIED if you change it.</span>
                      </>
                    }
                  />
                  <div className="is-flex">
                    <div className="is-flex-grow-1 mr-1">
                      <TextInput
                        name="sampleDate"
                        label={`Sample Date (currently: "${initialValues.dob}"). YYYY-MM-DD`}
                      />
                    </div>

                    <div className="field is-flex-grow-1 ml-1">
                      <label className="label is-size-7 has-text-weight-light">
                        Sample Time (currently: {initialValues.sampleTimeHours.toString().padStart(2, '0')}:
                        {initialValues.sampleTimeMinutes.toString().padStart(2, '0')})
                      </label>
                      <div className="select">
                        <Field as="select" name="sampleTimeHours">
                          {range(0, 24).map((hour) => (
                            <option key={hour} value={hour}>
                              {hour.toString().padStart(2, '0')}
                            </option>
                          ))}
                        </Field>
                      </div>
                      <span className="is-size-4">&nbsp;:&nbsp;</span>
                      <div className="select">
                        <Field as="select" name="sampleTimeMinutes">
                          {range(0, 60).map((minute) => (
                            <option key={minute} value={minute}>
                              {minute.toString().padStart(2, '0')}
                            </option>
                          ))}
                        </Field>
                      </div>
                    </div>
                  </div>
                </section>
                <footer className="modal-card-foot is-justify-content-flex-end">
                  <button className="button" onClick={() => onRequestClose()}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={cx('button is-primary', {
                      'is-loading': isSubmitting,
                    })}
                  >
                    Amend details & resend certificate
                  </button>
                </footer>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </Modal>
  )
}

export default AmendAndResendCertificateModal
